import React, { Suspense } from 'react';
import {
  Icon,
  Table,
  Checkbox,
  Modal,
  Loader,
} from 'semantic-ui/semantic-ui-imports';
import IconButton from 'components/IconButton';
import './index.scss';
import * as typy from 'typy';
import { parseCapacityData } from 'services/results';
// import { toSvg } from 'html-to-image';
import { useTranslation } from 'react-i18next';
import {
  Aggregate,
  AggregateCategory,
  hasActive,
} from '../../Types/Aggregates';
import { useData } from '../../contexts/data';

const CapacityCurve = React.lazy(() => import('../CapacityCurve'));

export interface AggregateCalcuationProps {
  aggregate: Aggregate;
  loading?: boolean;
  categories?: Array<AggregateCategory>;
  onSelect?: (aggregate: Aggregate) => void;
  activeAggregate?: Aggregate;
}

interface ModalContent {
  title: string;
  content?: any;
}

const AggregateCalcuation = ({
  aggregate,
  categories,
  onSelect,
  activeAggregate,
  loading,
}: AggregateCalcuationProps) => {
  const [showModal, setShowModal] = React.useState(false);
  const [activeModal, setActiveModal] = React.useState<ModalContent | null>(
    null
  );
  const { setActiveAggregate, activeUserInputs } = useData();
  const { t } = useTranslation();
  const capacityCurveRef = React.createRef<HTMLDivElement>();

  const renderCapacityCurve = (props: any) =>
    props?.dataset && (
      <div className="capacity-curve">
        <div className="capacity-curve__graph">
          <CapacityCurve {...props} />
        </div>
        <ul>
          <li>{t('resultsPage.capacityCurve.descriptionBlack')}</li>
          <li>{t('resultsPage.capacityCurve.descriptionRedCircle')}</li>
          <li>{t('resultsPage.capacityCurve.descriptionRedCross')}</li>
        </ul>
      </div>
    );

  const triggerCapacityCurveModal = (a: Aggregate) => {
    const props = a ? { dataset: parseCapacityData(a) } : {};
    setActiveModal({
      title: `Kapacitetskurve - ${a.name}`,
      content: (
        <Suspense fallback={<Loader />}>{renderCapacityCurve(props)}</Suspense>
      ),
    });
    setShowModal(true);
  };

  const getSystemValue = (product: Aggregate, cat: string): any => {
    let val = typy.t(product, cat).safeObject;

    if (cat === 'inputs.cooling.ambient.VIn') {
      val = activeUserInputs['cooling.ambient.VIn'];
    }

    if (!val && val !== 0 && !loading) {
      return '-';
    }

    if (!val && val !== 0) {
      return '-';
    }

    if (
      (cat === 'system_data.heating.supply.T_out' ||
        cat === 'system_data.cooling.supply.T_out') &&
      val !== 0
    ) {
      val -= 273.15;
    } else if (
      cat === 'system_data.heating.Qc' ||
      cat === 'system_data.cooling.Qe'
    ) {
      val /= 1000;
    }

    if (val && val !== 0) {
      val = parseFloat(val).toFixed(2);
    }
    if (cat === 'system_data.heating.Qc' || cat === 'system_data.cooling.Qe') {
      val += ' kW';
    } else if (
      cat === 'system_data.heating.supply.T_out' ||
      cat === 'system_data.cooling.supply.T_out'
    ) {
      val = parseFloat(val).toFixed(1);
      val += ' °C';
    } else if (cat === 'system_data.cooling.Lp') {
      val = Math.round(val);
      val += ' dB(a)';
    } else if (cat === 'system_data.heating.SFP') {
      val = Math.round(val);
      val += ' J/m3';
    } else if (cat === 'system_data.heating.heat_reclaim') {
      const showHeatReclaim = typy.t(
        product,
        'system_data.heating.show_heat_reclaim'
      ).safeObject;
      if (showHeatReclaim) {
        val = (val * 100).toFixed(1);
        val += ' %';
      } else {
        val = '-';
      }
    } else if (
      cat === 'system_data.heating.COP' ||
      cat === 'system_data.cooling.EER'
    ) {
      const showCOP = hasActive(product);
      if (showCOP) {
        val = parseFloat(val).toFixed(1);
      } else {
        val = '-';
      }
    } else if (cat === 'inputs.cooling.ambient.VIn') {
      val = `${parseInt(val, 10)} ㎥/h`;
    }
    return val;
  };

  const saveAsSVG = async () => {
    if (capacityCurveRef.current) {
      const el = capacityCurveRef.current.querySelector(
        '.capacity-curve__graph svg'
      );
      if (el) {
        let html = el.outerHTML;
        html = html.split('</svg>').join(`
          <style>
            text {
              font-family: 'Lato', 'Helvetica Neue', Arial, Helvetica, sans-serif;
            }
          </style></svg>
        `);
        const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
          html
        )}`; // await toSvg(el as HTMLElement, {skipFonts: true, filter: (node) => node.tagName !== 'style'});
        const a = document.createElement('a');
        a.href = url;
        a.download = `capacity-curve-${new Date().getTime()}.svg`;
        a.click();
      }
    }
  };

  const hasAllKeys = (a: Aggregate) => !!Object.keys(a.system_data).length;

  return (
    <>
      <Table.Row
        style={{ cursor: 'pointer' }}
        onClick={() => {
          if (loading || aggregate.error) {
            return;
          }

          if (onSelect) {
            onSelect(aggregate);
            setActiveAggregate(aggregate);
          }
        }}
        disabled={aggregate.error}
      >
        {categories &&
          categories.map((cat) => (
            <React.Fragment key={cat.key}>
              {cat.key === 'name' && (
                <Table.Cell className="table-cell">
                  {onSelect && !loading && hasAllKeys(aggregate) && (
                    <Checkbox
                      checked={
                        activeAggregate && aggregate.id === activeAggregate.id
                      }
                      onClick={() => {
                        onSelect(aggregate);
                        setActiveAggregate(aggregate);
                      }}
                      name="product"
                    />
                  )}
                  {loading && (
                    <Loader
                      style={{ marginRight: '1rem' }}
                      size="tiny"
                      active={loading}
                      inline
                    />
                  )}
                  {!loading && aggregate.error && (
                    <Icon
                      style={{ marginRight: '0.9rem' }}
                      name="warning sign"
                    />
                  )}
                  {aggregate.name}
                </Table.Cell>
              )}
              {cat.key === 'kap' && (
                <Table.Cell>
                  {!loading && !aggregate.error && (
                    <Icon
                      name="line graph"
                      link
                      onClick={(ev: React.MouseEvent) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        triggerCapacityCurveModal(aggregate);
                      }}
                    />
                  )}
                </Table.Cell>
              )}
              {cat.key !== 'name' && cat.key !== 'kap' && (
                <Table.Cell>{getSystemValue(aggregate, cat.key)}</Table.Cell>
              )}
            </React.Fragment>
          ))}
      </Table.Row>
      <Modal
        onClose={() => {
          setShowModal(false);
          setActiveModal(null);
        }}
        open={showModal}
        closeIcon
        className="product-table__modal"
      >
        <Modal.Header className="product-table__modal__header">
          {activeModal?.title}
          <IconButton icon="download" onClick={() => saveAsSVG()} />
        </Modal.Header>
        <Modal.Content className="product-table__modal__content">
          <div ref={capacityCurveRef}>{activeModal?.content}</div>
        </Modal.Content>
      </Modal>
    </>
  );
};

AggregateCalcuation.defaultProps = {
  categories: [
    { name: 'name', key: 'name' },
    { name: 'KAP', key: 'kap' },
    { name: 'SFP', key: 'system_data.heating.SFP' },
    { name: 'VGV', key: 'system_data.heating.heat_reclaim' },
    { name: 'COP', key: 'system_data.heating.COP' },
    { name: 'heatingKAP', key: 'system_data.heating.Qc' },
    { name: 'supplyTOUT', key: 'system_data.heating.supply.T_out' },
    { name: 'coolEER', key: 'system_data.cooling.EER' },
    { name: 'coolKAP', key: 'system_data.cooling.Qe' },
    { name: 'coolingTOUT', key: 'system_data.cooling.supply.T_out' },
    { name: 'coolingLP', key: 'system_data.cooling.Lp' },
  ],
};

export default AggregateCalcuation;
